/**
 * TakeawayBar — Cockpit single-line italic summary banner
 * Authority: WI-UI-CLEANUP-001 Stage 2, SA-2 file ownership
 *
 * Props:
 *   text    : string   — the italic sentence to display
 *   loading : boolean  — when true, render shimmer skeleton instead
 *
 * Visual spec:
 *   - Height 40px min, background DS_COLORS.surface.warm (#F0F0F8)
 *   - Pulsing 8px navy dot on the left (CSS @keyframes pulse 2s infinite)
 *   - 15px Inter italic, DS_COLORS.text.brand (#191970), line-height 1.4
 *   - Padding 0 40px
 *   - role="status" aria-live="polite" for screen-reader announcements
 */

import React from 'react';
import { DS_COLORS, DS_TYPOGRAPHY } from '@/config/ui-invariants';
import { Skeleton } from '@/components/ui/skeleton';

// Inline keyframe injection — minimal, one-time, no external CSS file needed.
// We inject once per module load; the <style> tag is idempotent-guarded.
const PULSE_STYLE_ID = 'cockpit-dot-pulse';
if (typeof document !== 'undefined' && !document.getElementById(PULSE_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = PULSE_STYLE_ID;
  style.textContent = `
    @keyframes cockpitDotPulse {
      0%,100% { opacity: 1; transform: scale(1); }
      50%      { opacity: 0.4; transform: scale(1.25); }
    }
  `;
  document.head.appendChild(style);
}

const FALLBACK_TEXT = 'Reviewing today’s inbox…';

export default function TakeawayBar({ text, loading }) {
  const displayText = text && text.trim() ? text : FALLBACK_TEXT;

  return (
    <div
      data-testid="cockpit-takeaway-bar"
      role="status"
      aria-live="polite"
      style={{
        minHeight: '40px',
        background: DS_COLORS.surface.warm,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '0 40px',
      }}
    >
      {/* Pulsing dot */}
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: DS_COLORS.text.brand,
          display: 'inline-block',
          animation: 'cockpitDotPulse 2s ease-in-out infinite',
        }}
      />

      {loading ? (
        <Skeleton className="h-4 w-1/2" />
      ) : (
        <span
          style={{
            fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
            fontSize: '15px',
            fontStyle: 'italic',
            fontWeight: 400,
            color: DS_COLORS.text.brand,
            lineHeight: 1.4,
          }}
        >
          {displayText}
        </span>
      )}
    </div>
  );
}
